import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, apiFetch } from "@/lib/supabase";
// =============================================================================
// web/src/lib/geo.ts
//
// Mobilisation distance helper. Brisbane GPO is treated as the canonical
// "home base" for Coretel — all sites are reported as great-circle km from
// there. Matches app.bne_gpo_distance_km() in SQL so server-side and
// client-side calculations agree.
// =============================================================================

const BNE_GPO_LAT = -27.4679;
const BNE_GPO_LNG = 153.0251;
const EARTH_R_KM  = 6371;

/**
 * Great-circle distance from Brisbane GPO to the given coordinates in km,
 * rounded to 1 decimal. Returns null when lat/lng are missing.
 *
 * Haversine formula — accurate enough for mob-distance display. For actual
 * road distance you'd hit Google Maps / OSRM, but for our purposes this is
 * a good proxy and doesn't need an API call.
 */
export function distanceFromBneGpoKm(
  lat?: number | null,
  lng?: number | null
): number | null {
  if (lat == null || lng == null) return null;
  const dLat = ((lat - BNE_GPO_LAT) * Math.PI) / 180;
  const dLng = ((lng - BNE_GPO_LNG) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((BNE_GPO_LAT * Math.PI) / 180) *
      Math.cos((lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(EARTH_R_KM * c * 10) / 10;
}

/** Format a distance in km for display. */
export function fmtKm(km: number | null | undefined): string {
  if (km == null) return "—";
  if (km >= 100) return `${Math.round(km)} km`;
  return `${km.toFixed(1)} km`;
}

// -----------------------------------------------------------------------------
// Road distance (28 Sep 2026). James: "I put in these GPS coordinates and
// Brisbane GPO and it's 1700 km but we have 1373 — how did we get this so
// wrong." We didn't: 1,373 is the straight line above. Mob is planned on road
// km, so a site now carries sites.road_km_from_bne (mig 396) — fetched once
// through the gateway (OSRM, OpenStreetMap routing, no key) and saved by the
// first manager who looks at it. Until then the straight line shows, and
// says so.
// -----------------------------------------------------------------------------

export function useRoadKm(site: { site_id?: string | null; latitude?: number | null; longitude?: number | null; road_km_from_bne?: number | null } | null | undefined, canSave: boolean) {
  const qc = useQueryClient();
  const lat = site?.latitude ?? null, lng = site?.longitude ?? null;
  const stored = site?.road_km_from_bne ?? null;
  const q = useQuery({
    queryKey: ["road_km", site?.site_id, lat, lng],
    enabled: stored == null && lat != null && lng != null,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const r = await apiFetch("/geo/road-km", { method: "POST", body: JSON.stringify({ lat: Number(lat), lng: Number(lng) }) });
      const j = await r.json();
      const km = typeof j.road_km === "number" ? j.road_km : null;
      if (km != null && canSave && site?.site_id) {
        // best effort — RLS lets manager+ write the site; anyone else just sees the figure this once
        const { error } = await (supabase as any).from("sites").update({ road_km_from_bne: km, road_km_checked_at: new Date().toISOString() }).eq("site_id", site.site_id);
        if (!error) qc.invalidateQueries({ queryKey: ["site_detail", site.site_id] });
      }
      return km;
    },
  });
  return { roadKm: stored ?? q.data ?? null, checking: stored == null && q.isFetching, straightKm: distanceFromBneGpoKm(lat, lng) };
}
